import React, { useState, useEffect, useRef } from 'react';

const ChatSidebar = ({
  sessions = [],
  activeSessionId,
  onNewChat,
  onSelectSession,
  onDeleteSession,
  onRenameSession,
  isOpen,
  onToggle,
  onNavigate
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const editInputRef = useRef(null);

  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingId]);

  const filteredSessions = sessions.filter(session => 
    (session.title || 'New Search').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleStartEdit = (e, session) => {
    e.stopPropagation();
    setEditingId(session.id);
    setEditingTitle(session.title || 'New Search');
  };

  const submitEdit = () => {
    if (editingId && editingTitle.trim() !== '') {
      onRenameSession(editingId, editingTitle.trim());
    }
    setEditingId(null);
    setEditingTitle('');
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      submitEdit();
    } else if (e.key === 'Escape') {
      setEditingId(null);
      setEditingTitle('');
    }
  };

  const handleDelete = (e, sessionId) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this chat?')) {
      onDeleteSession(sessionId);
    }
  };

  const SidebarContent = (
    <div className="flex flex-col h-full bg-zinc-900 w-[260px]">
      {/* Top Section */}
      <div className="p-3">
        <div className="flex items-center justify-between mb-3 md:hidden">
          <div className="flex items-center gap-1.5 px-2">
            <img
              src="/logo-small.png"
              alt="LeadGen AI"
              className="h-6 w-auto object-contain"
            />
            <span className="font-semibold text-white">LeadGen<span className="text-blue-500"> AI</span></span>
          </div>
          <button 
            onClick={onToggle}
            className="p-1 rounded-lg hover:bg-zinc-800 text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <button 
          onClick={() => {
            onNewChat();
            if (window.innerWidth < 768 && onToggle) onToggle();
          }}
          className="w-full flex items-center justify-between px-3 py-2.5 bg-zinc-800 hover:bg-zinc-700 transition-colors rounded-lg text-gray-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/50"
        >
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>New Chat</span>
          </div>
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>

        <div className="relative mt-3">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search chats..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-zinc-800 rounded-lg py-2 pl-9 pr-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 transition-colors"
          />
        </div>
      </div>

      {/* Sessions List */}
      <div className="flex-1 overflow-y-auto px-3 pb-3">
        {sessions.length > 0 && <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider px-2 py-2 mt-1">Recents</h3>}
        
        <div className="space-y-1 mb-2">
          {filteredSessions.length === 0 ? (
            <div className="text-center px-4 py-8">
              <p className="text-sm text-gray-500">
                {sessions.length === 0 ? "No conversations yet. Start a new search!" : "No chats matched your search."}
              </p>
            </div>
          ) : (
            filteredSessions.map((session) => {
              const isActive = session.id === activeSessionId;
              const isEditing = session.id === editingId;

              return (
                <div 
                  key={session.id}
                  onClick={() => {
                    if (!isEditing) {
                      onSelectSession(session.id);
                      if (window.innerWidth < 768 && onToggle) onToggle();
                    }
                  }}
                  className={`group flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                    isActive ? 'bg-zinc-800 text-white border-l-2 border-blue-500 rounded-l-none -ml-px' : 'text-gray-300 hover:bg-zinc-800 border-l-2 border-transparent hover:text-white'
                  }`}
                >
                  <div className="flex-1 min-w-0 pr-2">
                    {isEditing ? (
                      <input
                        ref={editInputRef}
                        type="text"
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        onBlur={submitEdit}
                        onKeyDown={handleEditKeyDown}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full bg-zinc-700 text-white text-sm rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    ) : (
                      <div className="truncate text-sm pr-1 select-none">
                        {session.title || 'New Search'}
                      </div>
                    )}
                  </div>
                  
                  {!isEditing && (
                    <div className={`flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ${isActive ? 'opacity-100' : ''}`}>
                      <button 
                        onClick={(e) => handleStartEdit(e, session)}
                        className="p-1 text-gray-400 hover:text-white transition-colors rounded-md hover:bg-zinc-700 focus:outline-none"
                        title="Rename"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      <button 
                        onClick={(e) => handleDelete(e, session.id)}
                        className="p-1 text-gray-400 hover:text-red-400 transition-colors rounded-md hover:bg-zinc-700 focus:outline-none"
                        title="Delete"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Bottom Section */}
      <div className="p-3 ">
        <button 
          onClick={() => {
            onNavigate('/dashboard');
            if (window.innerWidth < 768 && onToggle) onToggle();
          }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-zinc-800 text-gray-300 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          </div>
          <span className="text-sm font-medium">Dashboard</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 md:hidden transition-opacity"
          onClick={onToggle}
        />
      )}

      {/* Sidebar - Mobile & Desktop */}
      <div 
        className={`fixed inset-y-0 left-0 z-50 transform ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } md:relative md:translate-x-0 transition-transform duration-300 ease-in-out h-full`}
      >
        {SidebarContent}
      </div>
    </>
  );
};

export default ChatSidebar;
